import React, { useState, ChangeEvent, useEffect } from 'react';
import {
    Container,
    Typography,
    TextField,
    IconButton,
    Button,
    Box,
    Paper,
    Stack,
    CircularProgress,
    Backdrop,
    Switch,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { MasterDataService } from '../../services/firebase/masterdataService';

export default function MasterDataPage() {
    const [saleItemTypes, setSaleItemTypes] = useState<string[]>(['']);
    const [foodCategories, setFoodCategories] = useState<string[]>(['']);
    const [isPlantBasedTreaty, setIsPlantBasedTreaty] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchMasterData = async () => {
            setIsLoading(true);
            try {
                const newSaleItemsTypes = await MasterDataService.fetchSaleItemsDiscountReason();
                setSaleItemTypes(newSaleItemsTypes.length ? newSaleItemsTypes : ['']);
                const newFoodCategories = await MasterDataService.fetchFoodCategories();
                setFoodCategories(newFoodCategories.length ? newFoodCategories : ['']);
                const newIsPlantBasedTreaty = await MasterDataService.fetchIsPlantBasedTreaty();
                setIsPlantBasedTreaty(newIsPlantBasedTreaty);
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchMasterData();
    }, []);

    const handleSaleItemTypeChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
        const newSaleItemTypes = [...saleItemTypes];
        newSaleItemTypes[index] = e.target.value;
        setSaleItemTypes(newSaleItemTypes);
    };

    const handleAddnewSaleItemType = () => {
        setSaleItemTypes([...saleItemTypes, '']);
    };

    const handleRemoveSaleItemType = (index: number) => () => {
        setSaleItemTypes(saleItemTypes.filter((_, i) => i !== index));
    };

    const handleSubmitSaleItemTypes = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            setSaleItemTypes([...saleItemTypes].filter((s) => s != ""))
            await MasterDataService.setSaleItemsDiscountReason([...saleItemTypes].filter((s) => s != ""));
            // Optionally show a snackbar/toast here
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFoodCategoryChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
        const newFoodCategories = [...foodCategories];
        newFoodCategories[index] = e.target.value;
        setFoodCategories(newFoodCategories);
    };

    const handleAddFoodCategoryType = () => {
        setFoodCategories([...foodCategories, '']);
    };

    const handleRemoveFoodCategoryType = (index: number) => () => {
        setFoodCategories(foodCategories.filter((_, i) => i !== index));
    };

    const handleSubmitFoodCategoryTypes = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            setFoodCategories([...foodCategories].filter((s) => s != ""))
            await MasterDataService.setFoodCategories([...foodCategories].filter((s) => s != ""));
            // Optionally show a snackbar/toast here
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 4, mb: 4, position: 'relative' }}>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Typography
                variant="h4"
                component="h1"
                gutterBottom
                align="center"
                sx={{ color: 'primary.dark' }}
            >
                Eco Eats Master Data
            </Typography>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Typography
                    variant="h6"
                    component="h2"
                    gutterBottom
                    align="center"
                    sx={{ color: 'primary.dark' }}
                >
                    Manage Food Categories
                </Typography>
                <Box component="form" onSubmit={handleSubmitFoodCategoryTypes}>
                    <Stack spacing={2}>
                        {foodCategories.map((value, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={`Field #${index + 1}`}
                                    value={value}
                                    onChange={handleFoodCategoryChange(index)}
                                    sx={{ mr: 1 }}
                                    disabled={isLoading}
                                />
                                <IconButton
                                    aria-label="remove field"
                                    onClick={handleRemoveFoodCategoryType(index)}
                                    color="error"
                                    disabled={isLoading}
                                >
                                    <RemoveCircleOutlineIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={handleAddFoodCategoryType}
                                sx={{ textTransform: 'none' }}
                                disabled={isLoading}
                            >
                                Add Field
                            </Button>
                            <Button
                                variant="contained"
                                type="submit"
                                sx={{ textTransform: 'none' }}
                                disabled={isLoading}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Paper>
            <Box sx={{ p: 2 }} />
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Typography
                    variant="h6"
                    component="h2"
                    gutterBottom
                    align="center"
                    sx={{ color: 'primary.dark' }}
                >
                    Manage Discount Reasons
                </Typography>
                <Box component="form" onSubmit={handleSubmitSaleItemTypes}>
                    <Stack spacing={2}>
                        {saleItemTypes.map((value, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label={`Field #${index + 1}`}
                                    value={value}
                                    onChange={handleSaleItemTypeChange(index)}
                                    sx={{ mr: 1 }}
                                    disabled={isLoading}
                                />
                                <IconButton
                                    aria-label="remove field"
                                    onClick={handleRemoveSaleItemType(index)}
                                    color="error"
                                    disabled={isLoading}
                                >
                                    <RemoveCircleOutlineIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={handleAddnewSaleItemType}
                                sx={{ textTransform: 'none' }}
                                disabled={isLoading}
                            >
                                Add Field
                            </Button>
                            <Button
                                variant="contained"
                                type="submit"
                                sx={{ textTransform: 'none' }}
                                disabled={isLoading}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Paper>
        </Container>
    );
}
